import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import DOMPurify from "dompurify";

/**
 *
 * @param inputs
 * @returns
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Checks if a specific tag exists within a string of tags.
 *
 * @param {string} tag - The tag to check for.
 * @param {string} tags - A string containing multiple tags.
 * @returns {boolean} - Returns `true` if the tag exists in the tags string, otherwise `false`.
 */
export const hasTag = (tag: string, tags: string): boolean => {
  // Ensure both parameters are strings
  if (typeof tag !== "string" || typeof tags !== "string") {
    return false;
  }
  // Check if the tag is included in the tags string
  return tags.includes(tag);
};

/**
 * Helper to capitalize the first letter of a string.
 * @param {string} str - String to capitalize.
 * @returns {string}
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Helper to get api url and
 * @param path
 * @param useProzApi
 * @param params
 * @returns string
 */
export const getApiBaseUrl = (
  path: string,
  useProzApi: boolean = false,
  params?: { [key: string]: string | number | boolean | undefined }
): string => {
  let urlParams = "";
  if (params && Object.keys(params).length > 0) {
    const validParams = Object.keys(params)
      .filter((item) => params[item] !== null && params[item] !== undefined)
      .map((item) => `${item}=${encodeURIComponent(String(params[item]))}`);
    urlParams = validParams.join("&");
  }

  let apiUrl = "";

  if (process.env.NEXT_PUBLIC_API_BASE_URL && !useProzApi) {
    apiUrl = `${process.env.NEXT_PUBLIC_API_BASE_URL}/${path}`;
    apiUrl = urlParams ? `${apiUrl}?${urlParams}` : apiUrl;
  } else {
    let protocol = "https:";
    let host = "api.proz.com";
    const apiVersion = "";
    if (process.env.NEXT_PUBLIC_NODE_ENV === "development") {
      protocol = "https:";
      host = "localhost:8443/next";
    }
    apiUrl = `${protocol}//${host}/${path}`;

    // ✅ Append query params here too
    apiUrl = urlParams ? `${apiUrl}?${urlParams}` : apiUrl;
  }

  return apiUrl;
};

export const getRedirectBaseUrl = (): string => {
  let host = "proz.com";
  let protocol = "https";

  if (process.env.NEXT_PUBLIC_NODE_ENV === "development") {
    protocol = "https"; // Corrected protocol format
    host = "localhost:8443";
  }

  // Ensure proper formatting of the base URL
  return `${protocol}://${host}`;
};

/**
 * Helper to construct proper notification redirect URLs
 * Handles the /next prefix correctly for both development and production
 * @param path - The path without leading slash (e.g., "opportunities", "profile/123/overview")
 * @returns Properly formatted URL with correct prefix
 */
export const getNotificationRedirectUrl = (path: string): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  // In development, the base URL already includes /next, so we don't add it again
  // In production, we use the standard path
  return `/${cleanPath}`;
};

/**
 *
 * @param str
 * @returns
 */
export const getFormattedBioHtml = (str: string): string => {
  str = DOMPurify.sanitize(str.replace(/\n/g, "<br />"), {
    ALLOWED_TAGS: ["a", "br"],
    ALLOWED_ATTR: ["href", "rel", "target"],
  });
  console.log(str);
  return str;
};

export const getWordCapacityLabel = (capacity?: string) => {
  if (!capacity) return null;

  switch (capacity) {
    case "millions":
      return "1,000,000+ words/day";
    case "hundreds_of_thousands":
      return "100,000+ words/day";
    case "tens_of_thousands":
      return "10,000+ words/day";
    case "thousands":
      return "1,000+ words/day";
    default:
      return null;
  }
};

/**
 * Takes date in format "2005-01-31T00:00:00.000Z" converts to format "Dec 31, 2003"
 * @param dateStr
 * @returns
 */
export const getFormattedDate = (dateStr: string): string => {
  const date = new Date(dateStr);

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);

  return formattedDate;
};

/**
 *
 * @param serialized
 * @returns
 */
export const parseSerializedPhp = (serialized: string): Record<string, string> | null => {
  if (!serialized) {
    return null;
  }
  const regex = /s:\d+:"(.*?)";s:\d+:"(.*?)";?/g;
  const matches = [...serialized.matchAll(regex)];

  return matches.reduce<Record<string, string>>((result, match) => {
    const [_, key, value] = match; // Extract key and value
    result[key] = value;
    return result;
  }, {});
};

/**
 * Creates a debounced version of a function, delaying its execution until after
 * a specified delay has elapsed since the last time the function was invoked.
 *
 * @template T - The type of the callback function.
 * @param {T} callback - The function to debounce.
 * @param {number} delay - The delay in milliseconds to wait before executing the callback.
 * @returns {(...args: Parameters<T>) => void} - A debounced version of the callback function.
 *
 * @example
 * const debouncedFunction = debounce((message: string) => {
 *   console.log(message);
 * }, 500);
 *
 */
// utils/debounce.ts
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/**
 *
 * @param name
 * @returns
 */
export const getInitials = (name: string): string => {
  if (!name) return "";
  const names = name.split(" ");
  const initials = names.map((n) => n.charAt(0).toUpperCase()).join("");
  return initials;
};

// Utility function to get a specific cookie by name
export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
};

// Utility function to delete a specific cookie by name
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
};

export const isRTL = (text: string): boolean => {
  const rtlCharRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlCharRegex.test(text);
};
